import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Divider, Paper, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";

import { CenteredLoading } from "@/features/ui";
import { GeneralInformationSection } from "./general-information-section";
import { OtherSection } from "./other-section";
import { PriceSummarySection } from "./price-summary-section";
import { SalesInvoicePaymentsSection } from "./sales-invoice-payments";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./sales-payment-form-constants";
import { SelectOutstandingPayableSection } from "./select-outstanding-payable-section";

export const SalesPaymentForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
  isLoading = false,
}) => {
  const methods = useForm({
    defaultValues: defaultValues
      ? { ...DEFAULT_VALUES, ...defaultValues }
      : DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const { handleSubmit, formState } = methods;

  const onFormSubmit = async (formData) => {
    // Editing a sales payment is a strict header-only replace: the API never
    // touches allocations, discounts or the grand total, and requires all three
    // header fields. An empty note clears it, so "" is sent rather than null.
    if (editMode) {
      await onSubmit({
        sellerId: formData.seller.id,
        transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
        note: formData.note ?? "",
        cashBankOutletChartOfAccountId: formData.outletChartOfAccount.id,
      });
      return;
    }

    const payload = {
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      sellerId: formData.seller.id,
      buyerableId: formData.buyer.id,
      buyerableType: formData.buyerableType,
      cashBankOutletChartOfAccountId: formData.outletChartOfAccount.id,
      note: formData.note || null,
      grandTotal: formData.grandTotal,
      salesInvoicePayments: formData.salesInvoicePayments.map((payment) => ({
        salesInvoiceId: payment.salesInvoiceId,
        allocatedAmount: payment.allocatedAmount,
        discountAccounts: (payment.discountAccounts ?? []).map((account) => ({
          outletChartOfAccountId: account.outletChartOfAccount.id,
          amount: account.amount,
          note: account.note || null,
        })),
      })),
    };

    await onSubmit(payload);
  };

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        spacing={2.5}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection editMode={editMode} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack spacing={2.5}>
              {!editMode && (
                <>
                  <SelectOutstandingPayableSection />
                  <Divider />
                </>
              )}
              <SalesInvoicePaymentsSection readOnly={editMode} />
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 7 }}>
          <OtherSection editMode={editMode} />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 5 }}>
          <PriceSummarySection />
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

SalesPaymentForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  editMode: PropTypes.bool,
  isLoading: PropTypes.bool,
};
